import {
  IProductSchemaUI,
  ProductStatusEnum,
  ProductTypeEnum,
  ProrationPolicyEnum,
} from '@monetize/types/app';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MFlex, MText } from '../../../components/Monetize';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import {
  PRODUCT_STATUS_ENUM_DISPLAY,
  PRODUCT_TYPE_DISPLAY,
  PRORATION_POLICY_DISPLAY,
} from '../../../constants/products';
import { getProductEditRoute } from '../../../constants/routes';

const columnHelper = createColumnHelper<IProductSchemaUI>();

export type ProductListColumnContext = ColumnContextBase<IProductSchemaUI>;

/** tableId: FilterStateKeys.PRODUCT_LIST */
export const PRODUCT_LIST_CATALOG: ColumnCatalog<
  IProductSchemaUI,
  ProductListColumnContext
> = [
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Name', 300),
        cell: nameWithIdBodyTemplate<IProductSchemaUI, unknown>({
          idProp: 'id',
          nameProp: 'name',
          idLinkFn: (id) => getProductEditRoute(id),
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'customId',
    label: 'Custom ID',
    build: () =>
      columnHelper.accessor('customId', {
        ...getColHeaderWithSize('Custom ID', 200),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'productType',
    label: 'Product Type',
    build: () =>
      columnHelper.accessor('productType', {
        ...getColHeaderWithSize('Product Type', 200),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            ProductTypeEnum,
            PRODUCT_TYPE_DISPLAY,
          ),
        }),
        cell: (data) => {
          const row = data.row.original;
          const value = PRODUCT_TYPE_DISPLAY[row.productType];
          if (
            row.productType === ProductTypeEnum.ADVANCE &&
            row.prorationPolicy === ProrationPolicyEnum.NON_PRORATED
          ) {
            return (
              <MFlex flexDirection="column">
                <MText maxW="fit-content">{value}</MText>
                <MText maxW="fit-content">
                  ({PRORATION_POLICY_DISPLAY[ProrationPolicyEnum.NON_PRORATED]})
                </MText>
              </MFlex>
            );
          }
          return <MText maxW="fit-content">{value}</MText>;
        },
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'sku',
    label: 'SKU',
    build: () =>
      columnHelper.accessor('sku', {
        ...getColHeaderWithSize('SKU', 180),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'prorationPolicy',
    label: 'Proration',
    build: () =>
      columnHelper.accessor('prorationPolicy', {
        ...getColHeaderWithSize('Proration', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            ProrationPolicyEnum,
            PRORATION_POLICY_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: PRORATION_POLICY_DISPLAY,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            ProductStatusEnum,
            PRODUCT_STATUS_ENUM_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: PRODUCT_STATUS_ENUM_DISPLAY,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        header: 'Updated',
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        header: 'Created',
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IProductSchemaUI>(),
];
